import { Box, Group, Paper, Stack } from '@mantine/core';
import type {
	MonitorActionNotification,
	MonitorActionSendEmail,
	MonitorActionSlack,
	MonitorNotificationTypes,
	NotificationRuleOut,
} from '@repo/api-codegen';
import { SettingsBlock, SettingsButton } from '@repo/common/components';
import {
	IconButton,
	Select,
	Switch,
	Text,
	TextArea,
	TextInput,
	Title,
} from '@repo/foundations';
import type { ISlackChannel } from '../../../api/types/models/slack';
import type { SearchFilterV2Store } from '../../../components/Filter';
import { MonitorEmailForm } from './MonitorEmailForm';
import { MonitorFilterConfigForm } from './MonitorFilterConfigForm';
import { MonitorNotificationEvents } from './MonitorNotificationEvents';
import { MonitorNotificationForm } from './MonitorNotificationForm';
import { MonitorSlackForm } from './MonitorSlackForm';

interface NotificationRuleFormProps {
	rule: NotificationRuleOut;
	onRuleUpdate: <K extends keyof NotificationRuleOut>(
		key: K,
		value: NotificationRuleOut[K]
	) => void;
	// Local state for immediate UI feedback
	localTitle: string;
	localDescription: string;
	onLocalTitleUpdate: (title: string) => void;
	onLocalDescriptionUpdate: (description: string) => void;
	filterStore: SearchFilterV2Store | null;
	// Email form props
	isAddingEmail: boolean;
	newEmail: string;
	emailError: string;
	onSetIsAddingEmail: (isAdding: boolean) => void;
	onSetNewEmail: (email: string) => void;
	onAddEmail: () => void;
	onRemoveEmail: (email: string) => void;
	// Slack form props
	isAddingSlackChannel: boolean;
	onSetIsAddingSlackChannel: (isAdding: boolean) => void;
	availableSlackChannels: ISlackChannel[] | undefined;
	onClose: () => void;
}

export function NotificationRuleForm({
	rule,
	onRuleUpdate,
	localTitle,
	localDescription,
	onLocalTitleUpdate,
	onLocalDescriptionUpdate,
	filterStore,
	isAddingEmail,
	newEmail,
	emailError,
	onSetIsAddingEmail,
	onSetNewEmail,
	onAddEmail,
	onRemoveEmail,
	isAddingSlackChannel,
	onSetIsAddingSlackChannel,
	availableSlackChannels,
	onClose,
}: NotificationRuleFormProps) {
	const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onLocalTitleUpdate(e.target.value);
	};

	const handleDescriptionChange = (
		e: React.ChangeEvent<HTMLTextAreaElement>
	) => {
		onLocalDescriptionUpdate(e.target.value);
	};

	const handleEnabledChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onRuleUpdate('is_enabled', e.target.checked);
	};

	const handlePriorityChange = (value: string | null) => {
		if (value) {
			onRuleUpdate('priority', parseInt(value, 10));
		}
	};

	const handleNotificationEventsChange = (events: MonitorNotificationTypes) => {
		onRuleUpdate('enabled_types', events);
	};

	const handleNotificationConfigChange = (
		config: MonitorActionNotification | undefined
	) => {
		onRuleUpdate('action_notification', config);
	};

	const handleEmailConfigChange = (config: MonitorActionSendEmail) => {
		onRuleUpdate('action_send_email', config);
	};

	const handleSlackConfigChange = (config: MonitorActionSlack) => {
		onRuleUpdate('action_slack', config);
	};

	const handleMatchAllChange = (value: boolean) => {
		onRuleUpdate('match_all', value);
	};

	return (
		<Box>
			<Group mb="md" spacing="xs">
				<IconButton
					iconName="chevronLeft"
					variant="tertiary"
					onClick={onClose}
					tooltip="Back to notification rules"
				/>
				<Title size="md">Monitors</Title>
			</Group>

			<Text weight="bold" size="md" mb="lg" mt="md">
				Details
			</Text>

			<Paper withBorder p="md" radius="md" mb="4xl">
				<Stack spacing="lg">
					<TextInput
						label="Rule Name"
						value={localTitle}
						onChange={handleTitleChange}
						placeholder="Enter rule title"
					/>

					<TextArea
						optional={true}
						label="Description"
						value={localDescription}
						onChange={handleDescriptionChange}
						placeholder="Optional description"
					/>

					<Switch
						label="Enabled"
						labelPosition="right"
						checked={rule.is_enabled}
						onChange={handleEnabledChange}
					/>

					<Stack spacing="xs">
						<Select
							label="Priority"
							value={rule.priority?.toString() || '10'}
							onChange={handlePriorityChange}
							data={[
								{ value: '20', label: 'High' },
								{ value: '10', label: 'Normal' },
								{ value: '5', label: 'Low' },
							]}
						/>
						<Text size="sm">
							If a monitor matches multiple rules, the rule with the highest
							priority will be chosen
						</Text>
					</Stack>
				</Stack>
			</Paper>

			<Text weight="bold" size="md" mb="lg" mt="md">
				Filter
			</Text>

			{filterStore && (
				<Box mb="4xl">
					<MonitorFilterConfigForm
						store={filterStore}
						matchingMonitorsCount={rule.matching_monitors_count}
						matchAll={rule.match_all}
						onMatchAllChange={handleMatchAllChange}
					/>
				</Box>
			)}

			<Text weight="bold" size="md" mb="lg" mt="md">
				Notify
			</Text>

			<MonitorNotificationEvents
				value={
					rule.enabled_types || {
						incident_create: true,
						incident_acknowledge: true,
						incident_resolve: true,
						incident_normal: true,
					}
				}
				onChange={handleNotificationEventsChange}
			/>

			<MonitorNotificationForm
				value={rule.action_notification as MonitorActionNotification}
				onChange={handleNotificationConfigChange}
			/>

			<MonitorEmailForm
				value={rule.action_send_email as MonitorActionSendEmail}
				onChange={handleEmailConfigChange}
				isAddingEmail={isAddingEmail}
				newEmail={newEmail}
				emailError={emailError}
				onSetIsAddingEmail={onSetIsAddingEmail}
				onSetNewEmail={onSetNewEmail}
				onAddEmail={onAddEmail}
				onRemoveEmail={onRemoveEmail}
			/>

			<MonitorSlackForm
				value={rule.action_slack as MonitorActionSlack}
				onChange={handleSlackConfigChange}
				isAddingSlackChannel={isAddingSlackChannel}
				onSetIsAddingSlackChannel={onSetIsAddingSlackChannel}
				availableSlackChannels={availableSlackChannels}
			/>

			<Box mb="4xl">
				<SettingsButton
					variant="tertiary"
					title="Incidents"
					tooltip="Incident configuration settings"
					aria-label="Incident configuration settings"
				/>

				<SettingsBlock
					title=""
					description={
						<Switch
							label="Disable incident creation"
							description="Disable creation of incidents. This will also disable all notifications."
							labelPosition="right"
							checked={rule.disable_incidents}
							onChange={(e) =>
								onRuleUpdate('disable_incidents', e.target.checked)
							}
						/>
					}
				/>
			</Box>
		</Box>
	);
}
